import React, { useEffect, useState } from 'react';
import { Check, Plus, Loader2 } from 'lucide-react';
import SheetOrPage from './SheetOrPage';
import { SpecialistIcon } from './specialistIcons';
import AgentStatCard from './AgentStatCard';
import { getAgentBio, type AgentBio } from '../../services/agentService';
import { TYPE } from '../../constants/typeScale';

interface Props {
  open: boolean;
  asPage?: boolean;
  /** Catalog agent id — fetches the full bio. Null/undefined for a custom agent (shows basics only). */
  profileId?: string | null;
  name: string;
  persona: string;
  description?: string | null;
  avatarIcon?: string | null;
  avatarUrl?: string | null;
  selected: boolean;
  /** False when the group is full and this agent isn't already in it. */
  canAdd: boolean;
  onToggle: () => void;
  onClose: () => void;
}

const Chips: React.FC<{ items: string[] }> = ({ items }) => (
  <div className="flex flex-wrap gap-1.5">
    {items.map((c) => (
      <span key={c} className={`inline-flex items-center rounded-full border border-secondary-200 dark:border-secondary-600 px-2 py-0.5 ${TYPE.meta} text-secondary-600 dark:text-secondary-200`}>{c}</span>
    ))}
  </div>
);

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div>
    <p className={`mb-1 ${TYPE.label} text-secondary-400`}>{title}</p>
    {children}
  </div>
);

/**
 * Full agent profile preview — the un-truncated "who is this agent" card, reachable from the member
 * picker so the user can read the whole bio (personality, look, skills, what it knows) before adding
 * it. Mobile-friendly (full-screen page on a phone via SheetOrPage). Also toggles membership.
 */
const AgentPreviewCard: React.FC<Props> = ({ open, asPage, profileId, name, persona, description, avatarIcon, avatarUrl, selected, canAdd, onToggle, onClose }) => {
  const [bio, setBio] = useState<AgentBio | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !profileId) { setBio(null); return; }
    let alive = true;
    setLoading(true);
    getAgentBio(profileId)
      .then((b) => { if (alive) setBio(b); })
      .catch(() => { if (alive) setBio(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [open, profileId]);

  if (!open) return null;
  const desc = bio?.description || description || '';
  return (
    <SheetOrPage open={open} asPage={asPage} title="Agent profile" onClose={onClose} maxWidth="max-w-md">
      <div className="p-5 space-y-5">
        {/* Identity */}
        <div className="flex items-center gap-3">
          <div className="w-14 h-14 rounded-full bg-secondary-100 dark:bg-secondary-800 flex items-center justify-center shrink-0 overflow-hidden">
            {avatarUrl ? <img src={avatarUrl} alt="" className="w-full h-full object-cover" /> : <SpecialistIcon name={avatarIcon || undefined} className="w-6 h-6 text-secondary-600 dark:text-secondary-300" />}
          </div>
          <div className="min-w-0">
            <p className={`${TYPE.stageTitle} truncate text-secondary-900 dark:text-white`}>
              {name}{bio?.native_name && <span className="font-normal text-secondary-400"> · {bio.native_name}</span>}
            </p>
            <p className={`${TYPE.body} text-secondary-500 truncate`}>{persona}</p>
          </div>
        </div>

        {loading && <div className={`flex items-center gap-2 ${TYPE.control} text-secondary-400`}><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading full profile…</div>}

        {desc && <Section title="About"><p className={`${TYPE.body} text-secondary-700 dark:text-secondary-200 leading-relaxed`}>{desc}</p></Section>}
        {bio?.personality && <Section title="Personality"><p className={`${TYPE.body} text-secondary-700 dark:text-secondary-200 leading-relaxed`}>{bio.personality}</p></Section>}
        {bio?.appearance && <Section title="Appearance"><p className={`${TYPE.body} text-secondary-700 dark:text-secondary-200 leading-relaxed`}>{bio.appearance}</p></Section>}
        {bio?.style && <Section title="Style"><p className={`${TYPE.body} text-secondary-700 dark:text-secondary-200 leading-relaxed`}>{bio.style}</p></Section>}
        {!!bio?.skills?.length && <Section title="Skills"><Chips items={bio.skills} /></Section>}
        {!!bio?.capabilities?.length && <Section title="Capabilities"><Chips items={bio.capabilities} /></Section>}
        {!!bio?.knowledge?.length && <Section title="Knows about"><Chips items={bio.knowledge} /></Section>}
        {bio?.stats && (
          <div className="border-t border-secondary-200 dark:border-secondary-600 pt-4">
            <AgentStatCard stats={bio.stats} />
          </div>
        )}

        {/* Add / remove */}
        <button
          onClick={onToggle}
          disabled={!selected && !canAdd}
          className={`w-full inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 ${TYPE.sectionTitle} transition-colors disabled:opacity-40 ${selected
            ? 'border border-secondary-300 dark:border-secondary-600 text-secondary-700 dark:text-secondary-200 hover:border-secondary-400'
            : 'bg-secondary-900 dark:bg-white text-white dark:text-secondary-900 hover:bg-black dark:hover:bg-secondary-100'}`}>
          {selected ? <><Check className="w-4 h-4" /> In your group — tap to remove</> : <><Plus className="w-4 h-4" /> Add to group</>}
        </button>
      </div>
    </SheetOrPage>
  );
};

export default AgentPreviewCard;
